/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AgentSelect.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom agent selection component with server-side search functionality,
    for selecting agents efficiently even with thousands of agents.

 🔧 Notes:
    - Implements searchable dropdown with server-side search
    - Supports keyboard navigation (arrow keys, enter, escape)
    - Shows agent name and extension
    - Optimized for performance with server-side filtering
    - Integrates with form validation systems

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useCallback, useEffect, memo } from 'react';
import { ChevronDown, Search, User } from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { useI18n } from '../../hooks/useI18n';

interface AgentOption {
  id: number;
  name: string;
  extension: string;
  enabled: boolean;
}

interface AgentSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

export const AgentSelect: React.FC<AgentSelectProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
  disabled = false,
  required = false
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);

  // Load agents from server
  const loadAgents = useCallback(async (search: string = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        sortBy: 'name',
        sortOrder: 'asc',
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      const response = await fetch(
        buildApiUrl(`/api/telephony/agents?${params}`),
        {
          headers: getApiHeaders()
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          const mapped = result.data
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter((agent: any) => agent.enabled) // Only enabled agents
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((agent: any) => ({
              id: agent.id,
              name: agent.name || `Agent ${agent.id}`,
              extension: agent.extension || '',
              enabled: agent.enabled
            }));
          setAgents(mapped);
        }
      }
    } catch (error) {
      console.error('Failed to load agents:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial agents when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadAgents(searchQuery);
    }
  }, [isOpen, loadAgents]);

  // Load selected agent if value exists
  useEffect(() => {
    if (value && !selectedAgent) {
      const loadSelected = async () => {
        try {
          const response = await fetch(
            buildApiUrl(`/api/telephony/agents/${encodeURIComponent(value)}`),
            {
              headers: getApiHeaders()
            }
          );

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              const agent = result.data;
              setSelectedAgent({
                id: agent.id,
                name: agent.name || `Agent ${agent.id}`,
                extension: agent.extension || '',
                enabled: agent.enabled
              });
            }
          }
        } catch (error) {
          console.error('Failed to load selected agent:', error);
        }
      };

      loadSelected();
    }
  }, [value, selectedAgent]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      loadAgents(searchQuery);
    }, searchQuery ? 300 : 0);

    return () => clearTimeout(timer);
  }, [searchQuery, isOpen, loadAgents]);

  // Filter options based on search (client-side filtering for already loaded items)
  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) {
      return agents;
    }
    
    const query = searchQuery.toLowerCase();
    return agents.filter(agent => 
      agent.name.toLowerCase().includes(query) ||
      agent.extension.toLowerCase().includes(query) ||
      String(agent.id).includes(query)
    );
  }, [searchQuery, agents]);

  // Get selected label
  const selectedLabel = useMemo(() => {
    if (selectedAgent) {
      return `${selectedAgent.name} (${selectedAgent.extension})`;
    }
    if (value) {
      return value;
    }
    return placeholder || t('queues.form.fields.agent.select') || 'Select agent';
  }, [value, placeholder, selectedAgent, t]);

  // Handle selection
  const handleSelect = useCallback((agent: AgentOption) => {
    onChange(String(agent.id));
    setSelectedAgent(agent);
    setIsOpen(false);
    setSearchQuery('');
  }, [onChange]);

  // Handle keyboard
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  }, []);

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left">
            {loading ? t('common.loading') : selectedLabel}
          </span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={t('queues.form.fields.agent.searchPlaceholder') || 'Search agents...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {t('common.loading')}
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {t('queues.form.fields.agent.noAgentsFound') || 'No agents found'}
              </div>
            ) : (
              filteredAgents.map((agent) => (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => handleSelect(agent)}
                  className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                    flex items-center gap-2
                    ${value === String(agent.id) ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                >
                  <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate font-medium">{agent.name}</span>
                    {agent.extension && (
                      <span className="text-xs text-muted-foreground truncate">{agent.extension}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer with count */}
          {!loading && filteredAgents.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/30">
              {filteredAgents.length} {t('queues.form.fields.agent.agentsFound') || 'agents found'}
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};

export default memo(AgentSelect);

